// /src/utils/quickActions.ts
// Centralized logic to extract Quick Actions (buttons) from assistant text.
// Safely modular, no side effects. ChatContainer can import this without
// changing any existing behavior until it renders the results.

// Public type used by both the extractor and the QuickActionsBar UI.
export type QuickAction = {
  id: string;
  label: string;          // Button label shown to user (e.g., "Ready", "Finished", "5")
  value: string;          // Value inserted into the input (e.g., "ready", "finished", "5")
  shortcut?: string;      // Optional keyboard shortcut ("R", "F", "1".."0")
  group?: string;         // Optional grouping (e.g., "SUDS", "Scale0-10", "Yes/No", "Confirm", "HomeworkSelect")
  autoSend?: boolean;     // Keep false by default (we're not auto-sending yet)
  enforce?: boolean;      // When true, the input should enforce constraints (e.g., 0–10 only)
};

const _norm = (s: string) =>
  s.replace(/[“”]/g, '"').replace(/[‘’]/g, "'").replace(/\u00A0/g, " ");

// Detect a numeric rating range like "0–10", "1-10", "on a scale from 0 to 10", etc.
// Returns null if the detected max > 10 or min >= max (we cap for UX and avoid noise).
function detectScaleRange(text: string): { min: number; max: number } | null {
  const t = text.toLowerCase();

  // Prefer when the prompt actually talks about a scale/rating or anxiety context
  const hasScaleCtx =
    /\b(scale|rate|rating)\b/.test(t) ||
    /\bon a scale\b/.test(t) ||
    /\bhow (anxious|anxiety|uncomfortable)\b/.test(t); // ← added “uncomfortable”

  // Accept legends like "0 = not at all" / "0 means no anxiety" / "10 = extremely"
  const hasLegend =
    /\b0\b[^.\n]*(=|means)\s*(not|no)/i.test(t) ||
    /\b10\b[^.\n]*(=|means)\s*(very|extreme|extremely|most|worst|highest|imagine)/i.test(t);

  // Generic X–Y matcher (hyphen, en dash, em dash, or the word "to")
  const dash = "[–—-]";
  const rangeRe = new RegExp(
    String.raw`(\b\d{1,2}\b)(?:\s*\([^)]*\))?\s*(?:${dash}|to)\s*(\b\d{1,2}\b)(?:\s*\([^)]*\))?`,
    "i"
  );
  const m = t.match(rangeRe);
  if (!m) return null;

  const min = parseInt(m[1], 10);
  const max = parseInt(m[2], 10);
  if (Number.isNaN(min) || Number.isNaN(max)) return null;
  if (min >= max) return null;
  if (max > 10) return null; // we only render up to 10 buttons
  if (min < 0) return null;  // support 0..10 or 1..10

  // If there’s no context or legend, likely noise → ignore
  if (!hasScaleCtx && !hasLegend) return null;

  return { min, max };
}

// Helper: try to extract Homework options from a structured JSON block in the text.
// Expected shape (any of these keys work): { "homeworkOptions": [{ id|recordId|name, label? }] }
function extractHomeworkOptionsFromJson(text: string): QuickAction[] {
  try {
    const obj = JSON.parse(text);
    const opts = obj?.homeworkOptions;
    if (!Array.isArray(opts) || opts.length === 0) return [];
    const actions: QuickAction[] = [];
    for (let i = 0; i < opts.length; i++) {
      const opt = opts[i] ?? {};
      const label = String(opt.label ?? i + 1);
      const value = label; // ← CRITICAL: send the number the user would type
      actions.push({
        // keep uniqueness by embedding any id/name in the button id (not the value)
        id: `hw-json-${i}-${String(opt.id ?? opt.recordId ?? opt.name ?? label)}`,
        label,
        value,                 // "1", "2", ...
        group: "HomeworkSelect",
      });
    }
    return actions;
  } catch {
    return [];
  }
}

// Helper: parse enumerated homework lists from plain text when context suggests selection.
// Matches lines like "1. Exposure A", "2) Exposure B", "- 3) Exposure C"
// Requires at least 2 items AND a "choose/select/pick" or "homework/assignment/exposure/exercise/task" cue.
function extractHomeworkOptionsFromEnumerated(text: string): QuickAction[] {
  const lines = text.split(/\r?\n/);

  const enumRx = /^\s*(?:[-*]\s*)?(\d{1,2})[.)]\s+(.+?)\s*$/;
  const enumerated: { n: string; name: string }[] = [];

  for (const line of lines) {
    const m = line.match(enumRx);
    if (m) {
      const n = m[1].trim();        // "1", "2", ...
      const name = m[2].trim();     // "Exposure A"
      if (name) enumerated.push({ n, name });
    }
  }

  if (enumerated.length < 1) return [];

  // Context cues to avoid false positives
  const selectionCtx = /\b(choose|select|pick|type the number|number corresponding)\b/i.test(text);
  const homeworkCtx = /\b(homework|assignment|exposure|exercise|task|option)s?\b/i.test(text);

  if (!selectionCtx && !homeworkCtx) return [];

  // We intentionally DO NOT send recordIds here — value must be what the user would type (the number)
  const actions: QuickAction[] = [];
  for (const { n } of enumerated) {
    actions.push({
      id: `hw-text-${n}`,
      label: n,   // shows on the button
      value: n,   // ← CRITICAL: send "1", "2", ...
      group: "HomeworkSelect",
    });
  }
  return actions;
}

// Main extractor: returns a small, deduped list of QuickActions
//export function extractQuickActionsFromText(text?: string): QuickAction[] {
export function extractQuickActionsFromText(
  text?: string,
  ctx?: { awaitKey?: string | null }
): QuickAction[] {
  if (!text) return [];
  const actions: QuickAction[] = [];
  const lower = text.toLowerCase();

  // 1) YES / NO for start/confirm style questions
  const yesNoStart =
    /\b(would you like to|do you want to)\s+(begin|start)\b/i.test(text) ||
    /\bbegin this (homework|exercise|task) now\b/i.test(text) ||
    /\bsay\s*['"]?yes['"]?\b/i.test(lower) ||
    /\b(should we|do you want to).*\?\b/i.test(lower);

  // Observed-coping confirmation has its own dedicated
  // "Yes, I did" quick action below. Do not also render
  // the generic Yes / No pair.
  const isObservedCopingConfirmation =
    (ctx?.awaitKey ?? "").trim().toUpperCase() === "COPING" &&
    /\bit looks like you may have practiced\b/i.test(text) &&
    /\byes,\s*i did\b/i.test(_norm(text));

  if (yesNoStart && !isObservedCopingConfirmation) {
    actions.push(
      { id: "yes", label: "Yes", value: "yes", shortcut: "Y", group: "Yes/No" },
      { id: "no", label: "No", value: "no", shortcut: "N", group: "Yes/No" },
    );
  }

  // 2) READY cue — only show when the assistant explicitly requests readiness
  const readyText = _norm(text)
    .replace(/\*\*/g, "")
    .replace(/__/g, "");

  const explicitReadyQuestion =
    /\bare you ready\??\b/i.test(readyText) ||
    /\b(?:say|type|click|tap|press)(?:\s+or\s+(?:say|type|click|tap|press))?\s*["']?\s*ready\s*["']?\b/i.test(
      readyText
    ) ||
    /\bready when you (?:are|want to start)\b/i.test(readyText);

  if (explicitReadyQuestion) {
    actions.push({
      id: "ready",
      label: "Ready",
      value: "ready",
      shortcut: "R",
      group: "Confirm",
    });
  }

  // 3) FINISHED cue (robust to smart quotes, punctuation, and UX copy changes)
  // ✅ Regression fix: DO NOT infer Finished just from "When done" unless "finished" is actually the completion token.
  // ✅ Also suppress Finished when the assistant explicitly instructs "click/tap Send" (Q&A steps).
  {
    const finText = _norm(text);
    const finLower = finText.toLowerCase();

    // If the assistant says: "When done, click Send." (Q&A), we should NOT show a Finished button.
    const sendInstruction =
      /\b(click|tap|press|hit)\s+(the\s+)?send\b/i.test(finLower) ||
      /\bclick\s+send\b/i.test(finLower);

    // Accept: "say Finished", "click Finished", "say or click Finished"
    const finishedCommand =
      /\b(?:say|type|click)(?:\s+or\s+(?:say|type|click))?\s*["']?\s*finished\s*["']?\s*[.!?]*/i;

    // Accept: "When done", "Once you're finished", etc.
    const hasWhenDone =
      /\b(?:when|once|after)\s+(?:you\s+are\s+|you're\s+|you’re\s+)?(?:done|finished)\b/i.test(finText);

    // Critical tightening:
    // - "When done" alone should NOT trigger Finished.
    // - Only trigger on "When done ... finished" (i.e., finished is actually mentioned),
    //   OR on an explicit finished command.
    const mentionsFinishedWord = /\bfinished\b/i.test(finText);

    const finishedCue =
      finishedCommand.test(finText) || (hasWhenDone && mentionsFinishedWord);

    if (finishedCue && !yesNoStart && !sendInstruction) {
      actions.push({
        id: "finished",
        label: "Finished",
        value: "finished",
        shortcut: "F",
        group: "Confirm",
      });
    }
  }

  // 4) NUMERIC SCALE (SUDS-style)
  const range = detectScaleRange(text);

  if (range) {
    for (let i = range.min; i <= range.max; i++) {
      actions.push({
        id: `scale-${i}`,
        label: String(i),
        value: String(i),
        shortcut: String(i % 10),
        group: range.min === 0 ? "Scale0-10" : "SUDS",
        enforce: true,
      });
    }
  } else {
    // Fallback: explicit "SUDS" + 1–10 phrasing
    const mentionsSUDS = /\bsuds\b/i.test(text);
    const mentionsRange =
      /(1\s*[-–—to]\s*10|1\s*through\s*10|1\s*\.\.\s*10|one\s*to\s*ten)/i.test(text);
    if (mentionsSUDS && mentionsRange) {
      for (let i = 1; i <= 10; i++) {
        actions.push({
          id: `suds-${i}`,
          label: String(i),
          value: String(i),
          shortcut: String(i % 10),
          group: "SUDS",
          enforce: true,
        });
      }
    }
  }

  // 4.5) HOMEWORK SELECTION (numbers only)
  {
    const jsonHomework = extractHomeworkOptionsFromJson(text);
    if (jsonHomework.length > 0) {
      actions.push(...jsonHomework);
    } else {
      const textHomework = extractHomeworkOptionsFromEnumerated(text);
      if (textHomework.length > 0) actions.push(...textHomework);
    }
  }

  // 4.6) COPING SELECTION (numbers only, when OCD v4 says it's a coping-choice prompt)
  // This catches lines like:
  //   1) Target Game
  //   2) Leaves on a Stream
  //
  // It ONLY activates when the agent intent is "COPING_QUERY" and awaitKey="coping_choice"
  // so it never interferes with other numeric parsing.
  {
    const copingSelectRx = /^\s*(\d+)[.)]\s+(.+)$/m;
    const hasCopingIntent =
      /\bintent\s*[:=]\s*["']?coping_query["']?/i.test(text) ||
      /\bcoping skills?\b/i.test(text);

    const awaitingCoping =
      /\bawaitKey\s*[:=]\s*["']?coping_choice["']?/i.test(text);

    if (hasCopingIntent && awaitingCoping) {
      const lines = text.split(/\r?\n/);
      const copingItems: { n: string; name: string }[] = [];

      for (const line of lines) {
        const m = line.match(copingSelectRx);
        if (m) {
          const n = m[1].trim();
          const name = m[2].trim();
          if (name) copingItems.push({ n, name });
        }
      }

      if (copingItems.length >= 1) {
        for (const { n } of copingItems) {
          actions.push({
            id: `coping-${n}`,
            label: n,     // what appears on the button
            value: n,     // what the ChatInput receives when clicked
            group: "CopingSelect",
          });
        }
      }
    }
  }

  // 4.7) OBSERVED COPING SKILL CONFIRMATION
  //
  // Ask Coping Skills uses this one-skill wording:
  //   "It looks like you may have practiced Leaves on a Stream earlier.
  //    Did you use that as a coping skill for this homework, or did you use something else?
  //    If yes, you can say 'yes, I did'."
  //
  // Render:
  //   [ Yes, I did ]
  //
  // IMPORTANT:
  // - The visible label is "Yes, I did"
  // - The value sent to the chat input is the ACTUAL coping skill name
  //   (e.g. "Leaves on a Stream")
  // - Only activate for awaitKey="COPING"
  // - Only activate for the singular "Did you use that..." wording.
  //   This intentionally does NOT trigger when multiple observed skills are listed.
  {
    const ak = (ctx?.awaitKey ?? "").trim().toUpperCase();
    const copingConfirmText = _norm(text);

    const observedSkillMatch = copingConfirmText.match(
      /\bit looks like you may have practiced\s+(.+?)\s+earlier\.\s+did you use that as a coping skill\b/i
    );

    const hasYesIDidCue =
      /\bif yes,\s*you can say\s*["']?\s*yes,\s*i did\s*["']?/i.test(
        copingConfirmText
      );

    if (
      ak === "COPING" &&
      observedSkillMatch &&
      hasYesIDidCue
    ) {
      const observedSkill = observedSkillMatch[1].trim();

      if (observedSkill) {
        actions.push({
          id: "coping-confirm-observed",
          label: "Yes, I did",
          value: observedSkill,
          group: "Confirm",
        });
      }
    }
  }

  // 5) NONE cue
  const _noneText = _norm(text);
  const noneCue =
    /\b(?:say|type|enter|reply|respond)\s*["']?\s*none\s*["']?\b/i.test(_noneText);

  // Robust gating: only allow None button for system awaitKeys (COPING / ALTERED)
  const ak = (ctx?.awaitKey ?? "").trim().toUpperCase();
  const allowNoneFromAwaitKey = ak === "COPING" || ak === "ALTERED";

  // If you want to keep legacy behavior when awaitKey is missing, set this to true.
  // If you want strict behavior, set to false.
  const LEGACY_FALLBACK = false;

  if (noneCue && (allowNoneFromAwaitKey || LEGACY_FALLBACK)) {
    actions.push({
      id: "none",
      label: "None",
      value: "none",
      shortcut: "N",
      group: "Confirm",
    });
  }

  // De-dupe by (label,value)
  const seen = new Set<string>();
  const out: QuickAction[] = [];
  for (const a of actions) {
    const key = `${a.label.toLowerCase()}::${a.value.toLowerCase()}`;
    if (!seen.has(key)) {
      seen.add(key);
      out.push(a);
    }
  }

  const homeworkActions = out.filter(
    (action) => action.group === "HomeworkSelect"
  );

  if (homeworkActions.length > 0) {
    return homeworkActions;
  }

  // Preserve the existing cap for non-homework quick actions.
  return out.slice(0, 12);
}
